import React, { ReactNode } from 'react';
import { Lightbulb, Home } from 'lucide-react';
import { useNavigate } from 'react-router';

export interface ModuleTab {
  key: string;
  label: string;
  icon?: ReactNode;
}

interface ModuleTabHeaderProps {
  title: string;
  icon?: ReactNode;
  tabs: ModuleTab[];
  activeTab: string;
  onTabChange: (key: string) => void;
  asideOpen?: boolean;
  onAsideToggle?: () => void;
  rightSlot?: ReactNode;
}

const ModuleTabHeader: React.FC<ModuleTabHeaderProps> = ({
  title,
  icon,
  tabs,
  activeTab,
  onTabChange,
  asideOpen = false,
  onAsideToggle,
  rightSlot,
}) => {
  const navigate = useNavigate();
  return (
    <div className="module-tab-header">
      <div className="module-tab-header-label">
        <button
          type="button"
          className="module-tab-header-home"
          aria-label="Home"
          title="Home"
          onClick={() => navigate('/dashboard')}
          data-testid="BTN_module_tab_header_home"
        >
          <Home size={16} strokeWidth={1.75} />
        </button>
        <span className="module-tab-header-sep" aria-hidden="true">/</span>
        {icon && <span className="module-tab-header-icon">{icon}</span>}
        <span className="module-tab-header-title">{title}</span>
      </div>

      <div className="module-tab-header-nav" role="tablist">
        {tabs.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            data-label={t.label}
            className={`module-tab-header-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => onTabChange(t.key)}
            data-testid={`TAB_module_tab_header_${t.key}`}
          >
            {t.icon && <span className="module-tab-header-tab-icon">{t.icon}</span>}
            {t.label}
          </button>
        ))}
      </div>

      <div className="module-tab-header-actions">
        {rightSlot}
        {onAsideToggle && (
          <button
            type="button"
            className={`module-tab-header-toggle${asideOpen ? ' open' : ''}`}
            aria-label="Insights"
            aria-expanded={asideOpen}
            title="Insights"
            onClick={onAsideToggle}
            data-testid="BTN_module_tab_header_insights_toggle"
          >
            <Lightbulb size={16} strokeWidth={1.75} />
            <span className="module-tab-header-actions-label">Insights</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ModuleTabHeader;
